/**
 * English copy, and the source of truth for every translation key.
 *
 * Other locales may be partial: `I18nProvider` falls back to these strings for
 * any key they do not define. Keys are grouped by the part of the app that
 * uses them. A string with `{placeholders}` also needs an entry in
 * `TranslationsWithParameters` (locales/types.ts) so `t()` knows its params.
 */
const en = {
	common: {
		Travelnest: 'Travelnest',
		back: 'Back',
		close: 'Close',
		edit: 'Edit',
		cancel: 'Cancel',
		ok: 'OK',
		showMore: 'Show more',
		showLess: 'Show less',
		loading: 'Loading',
		copied: 'Copied',
		copy: 'Copy',
	},
	Header: {
		hostedBy: 'hosted by',
		help: 'Help',
		backToProperty: 'Back to property',
	},
	PageFooter: {
		poweredBy: 'Powered by',
		termsAndConditions: 'Terms and conditions',
		privacyPolicy: 'Privacy policy',
	},
	feedback: {
		title: 'Get in touch',
		close: 'Close',
		back: 'Back',
		bugs: {
			title: 'Report a bug',
			subtitle: "Found something that isn't working? Let us know!",
			nameLabel: 'Name (optional)',
			namePlaceholder: 'Your name',
			emailLabel: 'Email (optional)',
			emailPlaceholder: 'Your email',
			messageLabel: 'What happened?',
			messagePlaceholder: 'Please describe what went wrong...',
			submit: 'Send bug report',
			submitting: 'Submitting...',
			successMessage: 'Thank you for your feedback!',
			errorMessage:
				'There was a problem submitting your feedback. Please try again.',
			validation: {
				messageRequired: 'Please tell us what happened',
				emailInvalid: 'Please enter a valid email address',
			},
		},
		help: {
			title: 'Get help',
			subtitle: "Need assistance? We're here to help!",
		},
	},
	property: {
		gallery: {
			showAll: 'Show all photos',
			photoOf: 'Photo of',
			previous: 'Previous photo',
			next: 'Next photo',
			title: 'Photos',
		},
		share: {
			label: 'Share',
			copied: 'Link copied',
		},
		location: {
			typeIn: '{type} in {place}',
			showOnMap: 'Show on map',
			title: 'Where you’ll be',
			openInMaps: 'Open in Google Maps',
			zoomIn: 'Zoom in',
			zoomOut: 'Zoom out',
		},
		capacity: {
			guests: '{count} guests',
			guest: '1 guest',
			bedrooms: '{count} bedrooms',
			bedroom: '1 bedroom',
			beds: '{count} beds',
			bed: '1 bed',
			bathrooms: '{count} bathrooms',
			bathroom: '1 bathroom',
		},
		about: {
			title: 'About this place',
		},
		amenities: {
			title: 'What this place offers',
			showAll: 'Show all amenities',
		},
		houseRules: {
			title: 'House rules',
		},
		reviews: {
			title: 'Guest reviews',
			count: '{count} verified reviews',
			showAll: 'Show all reviews',
			verifiedOn: 'Verified on',
			excellent: 'Excellent',
			veryGood: 'Very good',
			good: 'Good',
			starReviews: 'star reviews',
			daysAgo: 'days ago',
			monthsAgo: 'months ago',
			yearsAgo: 'years ago',
		},
	},
	booking: {
		title: 'Your trip',
		dates: 'Dates',
		checkIn: 'Check-in',
		checkOut: 'Check-out',
		addCheckIn: 'Add check-in',
		addCheckOut: 'Add check-out',
		guests: 'Guests',
		reserve: 'Reserve',
		save: 'Save',
		addDatesForPrice: 'Add dates for prices',
		bar: {
			total: '{amount} total',
			freeCancellation: 'Free cancellation',
		},
		noAvailability: 'No availability for selected dates',
		notChargedYet: 'You won’t be charged yet',
		selectDates: 'Select dates',
		calendar: {
			title: 'Select dates',
			checkIn: 'Check-in',
			checkOut: 'Check-out',
		},
		guestsPicker: {
			title: 'Guests',
			max: 'Maximum {count} guests',
			adults: 'Adults',
			adultsHint: 'Age 13+',
			children: 'Children',
			childrenHint: 'Ages 2 to 12',
			pets: 'Pets',
			petsHint: 'Up to {count}, with a pet fee',
		},
		summary: {
			nights: '{symbol}{price} x {nights} nights',
			night: '{symbol}{price} x 1 night',
			guests: '{count} guests',
			guest: '1 guest',
			minStay: 'Minimum stay is {nights} nights',
		},
		price: {
			title: 'Price details',
			nightSuffix: '/ night',
			total: 'Total ({currency})',
			perNight: '{price} / night',
			notApplicable: 'n/a',
		},
		discount: {
			title: 'Discount code',
			placeholder: 'Enter code',
			apply: 'Apply',
			enterCode: 'Enter a discount code',
			invalidCode: 'Enter a valid discount code',
			applied: '{amount} discount applied',
			willApply: '{amount} discount will be applied at checkout',
			notApplied: 'Discount not applied',
			expiredOrInvalid: 'It could be expired or invalid',
			exceedsTotal: 'Discount exceeds the total price',
		},
		cancellation: {
			freeUntil: 'Free cancellation until',
			nonRefundable: 'This reservation is',
			nonRefundableHighlight: 'non-refundable',
			chargedInFull:
				"If you cancel or don't show up, you'll pay the full price.",
			noRefundAfterWindow:
				"If you cancel after that or don't show up, you won't get a refund.",
		},
	},
	checkout: {
		title: 'Complete your booking',
		subtitle: 'You’re a few details away from securing your stay.',
		yourDetails: 'Your details',
		firstName: 'First name',
		lastName: 'Last name',
		email: 'Email',
		emailHint: 'Your booking confirmation and receipt will be sent here',
		address1: 'Address line 1',
		address2: 'Address line 2 (optional)',
		city: 'City',
		country: 'Country',
		state: 'State',
		postcode: 'Postcode / ZIP code',
		phone: 'Phone number',
		phoneHint: 'Include your country code, e.g. +44',
		selectCountry: 'Select a country',
		selectState: 'Select a state',
		payment: {
			title: 'Payment',
			intro: 'You will be charged the total price.',
			policy: '{policy} {highlight}. {footer}',
			continue: 'Continue to payment',
			modalTitle: 'Payment details',
			secure: 'Guaranteed safe & secure checkout',
		},
		terms: {
			text: 'By continuing, I acknowledge that I have reviewed the {privacy} and {travel} and have reviewed and accept the {terms}.',
			privacy: 'Privacy Statement',
			travel: 'Government Travel Advice',
			terms: 'Rules & Restrictions and Terms of Use',
		},
		error: {
			title: 'Something went wrong',
			description: 'We had a problem processing your payment.',
			tryAgain: 'Please check your card details are valid and try again.',
			back: 'Go back to checkout',
		},
		validation: {
			required: 'Required',
			tooShort: 'Too short',
			tooLong: 'Too long',
			invalidEmail: 'Enter a valid email address',
			invalidPhone:
				'Enter a phone number starting with your country code',
		},
		confirmed: {
			title: 'Your reservation is confirmed',
			description:
				'We are pleased to let you know that your booking at {property} is confirmed.',
			emailOnItsWay:
				'You will receive a confirmation email shortly with these details.',
			reference: 'Booking reference: {reference}',
			changeReservation: 'Make a change to this reservation',
			yourTrip: 'Your trip',
			address: 'Address',
			dates: 'Dates',
			guests: 'Guests',
			checkIn: 'Check-in',
			checkOut: 'Check-out',
			checkInFrom: 'From {time}',
			checkOutBy: 'By {time}',
			yourHost: 'Your host',
			name: 'Name',
			phone: 'Phone',
			email: 'Email',
			amenities: 'Amenities',
			payment: {
				title: 'Payment for this booking',
				approved:
					'Your payment card has been approved and we will process your payment in the next few days.',
				policy: '{policy} {highlight}. {footer} The cancellation policy at time of booking will apply, regardless of COVID19 or any new restriction that may arise.',
			},
			thanks: 'Thanks again for booking with us and we hope you have a lovely stay!',
		},
		missedOut: {
			title: 'We’re sorry, you’ve just missed out',
			description:
				'Someone else selected some or all of those dates at the same time as you, and completed checkout first.',
			pendingTransaction:
				'You may see a pending transaction from Travelnest in your online banking, but no funds will be taken. This transaction will disappear, usually within 24 hours.',
			tryAgain:
				'We’d still love to have you stay with us. Please select different dates to reserve again.',
			changeDates: 'Change dates and try again',
			genericError: 'Something went wrong',
			code: 'Code',
		},
		processing: 'Processing your payment…',
	},
	support: {
		pageTitle: 'Travelnest Direct support',
		title: 'Submit a support request',
		subtitle:
			'Tell us about your booking and we’ll get back to you by email.',
		enquiryType: 'Enquiry type',
		enquiryTypes: {
			amendment: 'Amendment',
			cancellation: 'Cancellation',
			general: 'General query',
		},
		bookingId: 'Booking ID',
		bookingIdHint: 'You’ll find this in your confirmation email',
		name: 'Name',
		email: 'Email',
		comments: 'How can we help?',
		send: 'Send request',
		sentHeading: 'Request sent',
		sent: 'Thanks, your enquiry has been sent. We’ll reply to {email}.',
		errorHeading: 'Something went wrong',
		error: 'We couldn’t send your enquiry. Please try again.',
		validation: {
			required: 'Required',
			bookingIdRequired: 'Please provide your booking ID',
			nameTooShort: 'Please enter your full name',
			commentsTooShort: 'Please tell us a little more',
			tooLong: 'Too long',
			emailInvalid: 'Please enter a valid email address',
		},
	},
	SecurityDeposit: {
		title: 'This property requires a {symbol}{securityDeposit} refundable security deposit, which will be collected separately by the host either before your arrival or at check-in.',
	},
	PaymentLink: {
		Header: {
			vrbo: 'Vrbo',
			changeLanguage: 'Change language',
		},
		'guestCount#one': '{count} guest',
		'guestCount#other': '{count} guests',
		StaySummary: {
			title: 'Your booking',
			reservationId: 'Reservation/Booking ID',
			checkInOut: 'Check-in/out',
			guests: 'Guests',
			total: 'Total for your stay',
		},
		PaymentDeadline: {
			reservedOnly:
				'Your dates are reserved until {deadline}. Pay by then to keep your booking.',
			reserved:
				'Your dates are reserved until {deadline}. Pay by then to keep your booking. Once paid, you can cancel for free until {freeCancellationDate}.',
			reservedNonRefundable:
				'Your dates are reserved until {deadline}. Pay by then to keep your booking. This booking is non-refundable.',
		},
		PaymentError: {
			title: 'Authentication failed',
			description:
				"We couldn't complete your payment because your bank didn't approve the 3D Secure check. Please try again or use a different payment method.",
			retry: 'Try payment again',
		},
	},
	GuestDetails: {
		title: 'Billing information',
		fullName: 'Full name (as on card)',
		billingAddress: 'Billing address',
		billingAddress2: 'Address line 2 (optional)',
		billingCity: 'City',
		billingPostcode: 'Postcode / ZIP code',
		billingCountry: 'Country',
		required: 'Required',
		contactSupport: "Questions about your payment? We're here to help. ",
		contactSupportLink: 'Contact Support',
	},
	PaymentDetails: {
		title: 'Payment',
		description:
			'Almost there! Add your card details to lock in your booking and get ready for your vacation.',
		addCardDetails: 'Add card details',
		secureCheckout: 'Guaranteed safe & secure checkout',
		cardDetails: 'Card details',
		cardIcons: {
			visa: 'Visa',
			mastercard: 'Mastercard',
			amex: 'American Express',
		},
	},
	confirmedSummary: {
		title: 'Your booking is confirmed',
		description:
			'We are pleased to let you know that your booking at {property} is confirmed.',
		confirmationEmail:
			'You will receive a confirmation email shortly with these details.',
		bookingDetails: 'Your booking details',
		yourBooking: 'Your booking',
		reservationId: 'Reservation/Booking ID',
		checkInOut: 'Check-in/out',
		guests: 'Guests',
		total: 'Total paid ({currency})',
		reservation: 'Reservation/Booking ID',
		property: 'Property ID',
		checkIn: 'Check-in',
		checkOut: 'Check-out',
		paymentForThisBooking: 'Payment for this booking',
		bankStatement:
			'Your payment will show up on your bank or card statement as MGP*Travelnest VRBO.',
		arrivalDetails:
			'To confirm your arrival details and arrange access to the property, please get in touch and say ‘Hi’ to the owner of this property before your stay. If you have a special request (e.g. to change the check-in time / bring a pet) or have a question about the property, please get in touch with your host.',
		email: 'Email',
		phone: 'Phone',
		address: 'Address',
		thanks: 'Thanks again for booking with us and we hope you have a lovely stay!',
		host: '{hostName} is your host',
		checkInOnline: {
			title: 'Check in online',
			description:
				'Your host needs a few details before you arrive. It takes under 5 minutes, and once you’re done we’ll send you your check-in details.',
			start: 'Check in now',
			doneTitle: 'You’re checked in',
			doneDescription:
				'Your host has your details. You can look over or change them before you arrive.',
			view: 'View your check-in',
		},
	},
	cancelledSummary: {
		title: 'Your booking has been cancelled',
		description: 'Your booking at {property} has been cancelled.',
		bookingDetails: 'Your booking details',
		yourBooking: 'Your booking',
		reservationId: 'Reservation/Booking ID',
		checkInOut: 'Check-in/out',
		guests: 'Guests',
		total: 'Total paid ({currency})',
		reservation: 'Reservation/Booking ID',
		thanks: 'We hope you book with us again soon; see more of our properties on {vrboLink}.',
		property: 'Property ID',
		checkIn: 'Check-in',
		checkOut: 'Check-out',
		paymentForThisBooking: 'Payment for this booking',
		vrboName: 'Vrbo',
	},
	checkIn: {
		head: {
			title: 'Check in for {property}',
		},
		opening: 'Opening your check-in…',
		title: "Let's get you checked in",
		lead: 'Your host {host} needs a few extra details before you arrive. Once you’re done, we’ll send you your check-in details.',
		leadNoHost:
			'Your host needs a few extra details before you arrive. Once you’re done, we’ll send you your check-in details.',
		steps: {
			progress: 'Step {current} of {total}',
			update: 'Update',
			next: 'Next',
			back: 'Back',
			start: 'Start check-in',
		},
		welcome: {
			yourHost: 'Your host',
			booked: {
				channel: 'Booked on {channel}',
				none: 'Your booking',
			},
			// Airbnb's own name for the reference, so it matches the guest's app.
			confirmationCode: 'Confirmation code {reference}',
			bookingReference: 'Booking reference {reference}',
			'nights#one': '{count} night',
			'nights#other': '{count} nights',
			sleeps: 'Sleeps {count}',
			typeSleeps: '{type} that sleeps {count}',
			hostedBy: 'Hosted by {host}',
			hostNote: 'Your answers go to your host only.',
			managedBy: 'Managed by Travelnest',
			duration: 'Takes under 5 minutes',
			verified: 'Verified',
			bioMore: 'Read more',
			bioLess: 'Show less',
			retention:
				'Your answers go to your host only and are deleted 30 days after your stay.',
			privacyPolicy: 'Privacy policy',
			messageHost: 'Message {host} on {channel}',
			shownAs: 'Shown as {travelnest} in the {channel} app',
			unsure: {
				title: 'Not sure what this is?',
				channel:
					'Travelnest manages this home on {channel} for {host}, so your booking and messages will show Travelnest as the host. Any messages you send reach {host} directly.',
				channelEmail:
					'Travelnest manages this home on {channel} for {host}, so your booking will show Travelnest as the host. We’ll email you everything you need before you arrive.',
				generic:
					'Travelnest manages this home for {host}. We’ll email you everything you need before you arrive.',
			},
		},
		stay: {
			title: 'Your stay',
			property: 'Property',
			dates: 'Dates',
			guests: 'Guests',
			reference: 'Booking reference',
		},
		leadGuest: {
			title: 'How can your host reach you?',
			intro: 'Your host will use these details to get in touch during your stay.',
			introChannel:
				'Your host will message you on {channel}, and keep these details in case anything urgent comes up.',
			firstName: 'First name',
			lastName: 'Last name',
			email: 'Email',
			phone: 'Phone number',
			phoneHint: 'Include your country code, e.g. +44',
			country: 'Country',
			selectCountry: 'Select a country',
		},
		address: {
			title: 'What is your home address?',
			intro: 'Your host uses this to comply with local hosting rules. It’s only used for that and your booking.',
			address1: 'Address line 1',
			address2: 'Address line 2 (optional)',
			city: 'Town or city',
			postcode: 'Postcode / ZIP code',
		},
		otherGuests: {
			title: 'Who else is staying?',
			intro: 'Let your host know who’s coming, so they can get the place ready for everyone.',
			adults: 'Other adults',
			adultsIntro: "18 or over. You're already counted.",
			children: 'Children',
			childrenIntro:
				'Anyone under 18. Ages help your host get the place ready.',
			noAdults: 'No other adults.',
			noChildren: 'No children.',
			fullName: 'Full name',
			firstName: 'First name',
			lastName: 'Last name',
			dateOfBirth: 'Date of birth',
			age: 'Age',
			adult: 'Adult',
			child: 'Child',
			childAged: 'Child aged {age}',
			addAdult: 'Add an adult',
			addChild: 'Add a child',
			adultNumber: 'Adult {number}',
			childNumber: 'Child {number}',
			removeAdult: 'Remove adult {number}',
			removeChild: 'Remove child {number}',
			sleeps: 'This property sleeps {count}, so no more guests can be added.',
			moreThanBooked:
				'Your booking is for {count} guests. Contact your host before adding anyone else.',
		},
		arrival: {
			title: 'When will you arrive?',
			intro: 'Let your host know roughly when to expect you, so everything’s ready when you get there.',
			time: 'Expected arrival time',
			selectTime: 'Select a time',
			vehicle: 'Car registration',
			vehicleHint: 'So your host can arrange parking.',
			car: 'Are you bringing a car?',
			carYes: 'Yes',
			carNo: 'No',
		},
		pets: {
			title: 'Are you bringing a pet?',
			intro: 'Your host likes to know what to expect.',
			yes: 'Yes',
			no: 'No',
			type: 'Type of pet',
			breed: 'Breed (optional)',
			add: 'Add another pet',
			remove: 'Remove pet {number}',
			petNumber: 'Pet {number}',
		},
		idDocument: {
			passport: 'Passport',
			drivingLicence: 'Driving licence',
			nationalId: 'National ID card',
			residencePermit: 'Residence permit',
		},
		review: {
			title: 'Check your answers',
			intro: 'Make sure everything is right, then agree to the house rules to finish.',
			editTitle: 'Edit your details',
			editIntro:
				'Check your details and update anything that doesn’t look right.',
			change: 'Change',
			changeSection: 'Change {section}',
			home: 'Home address',
			personal: 'About you',
			justYou: 'Just you',
			sections: {
				you: 'Your details',
				group: 'Your group',
				stay: 'Your stay',
			},
		},
		houseRules: {
			title: 'House rules',
			timesTitle: 'Arrival and departure',
			checkInFrom: 'Check-in from',
			checkOutBy: 'Check-out by',
			allowed: 'Allowed',
			notAllowed: 'Not allowed',
			subjects: {
				smoking: 'Smoking',
				pets: 'Pets',
				children: 'Children',
				infants: 'Infants',
				groups: 'Parties and events',
			},
			agree: 'I have read and agree to the house rules',
			agreeHint:
				'Your host asks every guest to follow these during the stay.',
		},
		submit: 'Complete check-in',
		saved: {
			title: 'Changes saved',
			description: 'Your host has your updated details.',
		},
		privacy:
			'Your details are shared with your host only, so they can prepare for your stay.',
		error: {
			title: 'We could not save your check-in',
			description:
				'Please check your connection and try again. If it keeps happening, contact your host.',
		},
		validation: {
			required: 'Required',
			tooShort: 'Too short',
			tooLong: 'Too long',
			invalidEmail: 'Enter a valid email address',
			invalidPhone:
				'Enter a phone number starting with your country code, e.g. +44',
			invalidTime: 'Select a time',
			houseRules: 'You need to agree to the house rules to check in',
			tooManyGuests: 'That is more guests than the property sleeps',
			invalidAge: 'Enter an age from 0 to {max}',
			invalidDate: 'Enter a real date',
			childBirthDate:
				'Children are under 18 on the first night. Add anyone older as an adult.',
			adultBirthDate: 'Under 18 on the first night? Add them as a child.',
			detailsConfirmed: 'Please confirm the details are correct',
		},
		details: {
			leadTitle: 'A few more details about you',
			guestTitle: '{name}’s details',
			intro: {
				ES: 'Spanish law asks hosts to register every guest with the police (SES.HOSPEDAJES).',
				PT: 'Portuguese law asks hosts to report every guest who is not Portuguese (SIBA).',
				IT: 'Italian law asks hosts to register every guest with the police (Alloggiati Web).',
				FR: 'French law asks hosts to keep a registration form for each guest who is not French.',
			},
			nationality: 'Nationality',
			sex: 'Sex (as on ID)',
			sexes: {
				female: 'Female',
				male: 'Male',
				other: 'Other',
			},
			dateOfBirth: 'Date of birth',
			dateParts: {
				day: 'Day',
				month: 'Month',
				year: 'Year',
			},
			birthCountry: 'Country of birth',
			birthPlace: 'Town of birth',
			documentType: 'ID document',
			documentNumber: 'Document number',
			placeOfIssue: 'Country of issue',
			issueTown: 'Town of issue',
			sameAddress: 'Lives at the same address as {name}',
			residenceCountry: 'Country of residence',
			relationship: 'Relationship to {name}',
			relationships: {
				child: 'Son or daughter',
				grandchild: 'Grandchild',
				sibling: 'Brother or sister',
				niece_nephew: 'Niece or nephew',
				in_care: 'In their care',
				other: 'Other',
			},
			select: 'Select',
			declaration: {
				title: 'I confirm these details are correct for everyone in my party',
				description:
					'This stands in for your signature on the guest register.',
			},
			review: {
				born: 'Born {date}',
				bornOnIn: 'Born {date} in {place}',
				nationality: 'Nationality: {country}',
				bornIn: 'Born in {place}',
				livesWithYou: 'Lives with you',
				livesIn: 'Lives in {place}',
				issuedIn: 'Issued in {country}',
			},
		},
		done: {
			submittedTitle: 'You’re all set, {name}!',
			submittedLead:
				'Thanks, we have passed your details to your host at {property}.',
			completedTitle: 'You’re all set, {name}!',
			completedLead:
				'You completed check-in for your stay at {property}.',
			stayTitle: 'Here’s your stay, {name}',
			stayTitleNoName: 'Here’s your stay',
			stayLead: 'Everything you need for {property}, in one place.',
			countdown: {
				// The number is shown large above this label; `count` only picks the plural.
				'daysToGo#one': 'day to go',
				'daysToGo#other': 'days to go',
				tomorrow: 'See you tomorrow!',
				today: 'Today’s the day!',
				during: 'Enjoy your stay!',
				after: 'We hope you had a great stay',
			},
			status: 'Checked in',
			stay: {
				checkIn: 'Check-in',
				checkOut: 'Check-out',
				party: 'Guests',
				booking: 'Booking',
				from: 'from {time}',
				by: 'by {time}',
				'nights#one': '{count} night',
				'nights#other': '{count} nights',
			},
			gettingThere: {
				title: 'Getting there',
				directions: 'Get directions',
				directionsHint: 'Opens in your maps app',
				copy: 'Copy address',
				copied: 'Address copied',
			},
			arrival: {
				title: 'How to get in',
				hostSays: '{host} says',
				yourHostSays: 'Your host says',
				from: 'Your check-in instructions will appear here on {date}, {days} days before you arrive. We’ll also send them to your {channel} messages.',
				fromEmail:
					'Your check-in instructions will appear here on {date}, {days} days before you arrive. We’ll also email them to you.',
				later: 'Your host will send you how to get in before you arrive. Not heard from them yet? Get in touch below.',
				methods: {
					key_safe: 'You’ll pick up the keys from a key safe.',
					keypad: 'The door has a keypad, so there are no keys to collect.',
					smart_lock:
						'The door has a smart lock, so there are no keys to collect.',
					host_greets: 'Your host will meet you to let you in.',
					key_collection:
						'You’ll collect the keys from another address.',
					hidden_spot:
						'The keys will be left somewhere safe for you.',
					reception:
						'Pick up your keys from reception or the concierge.',
					instructions_sent:
						'Your host will send you instructions before you arrive.',
					contact_us:
						'Get in touch with your host to arrange how you’ll get in.',
				},
			},
			host: {
				title: 'Need anything before you arrive?',
				intro: 'Questions about getting there or settling in? {host} is happy to help.',
				introNoName:
					'Questions about getting there or settling in? Your host is happy to help.',
				message: 'Message on {channel}',
				messageHint: 'Shown as Travelnest in the {channel} app',
				messageNoLink:
					'Message your host in the {channel} app, where your booking is.',
				call: 'Call',
				whatsapp: 'WhatsApp',
				email: 'Email',
				phoneHint: 'Message your host directly',
				noContact: 'Your host will be in touch before you arrive.',
			},
			support: {
				title: 'Can’t reach your host?',
				intro: 'If you can’t get hold of {host} and you’re arriving soon, Travelnest Guest Support is here for you. We’ll get it sorted.',
				introNoName:
					'If you can’t get hold of your host and you’re arriving soon, Travelnest Guest Support is here for you. We’ll get it sorted.',
				quote: 'Just mention your {channel} booking {reference}.',
				quoteNoChannel: 'Just mention booking {reference}.',
				chat: 'Live chat',
				call: 'Call us',
			},
			detailsTitle: 'Your details',
			editableUntil:
				'You can change your answers until the day you arrive.',
			locked: 'Your stay has started, so these details can no longer be changed. Contact your host if something is wrong.',
			edit: 'Edit your details',
			houseRulesNote: 'A few things that help everyone enjoy the stay.',
			contact: 'Contact',
			arrivalTime: 'Expected arrival',
			vehicle: 'Car',
			noVehicle: 'No car',
			pets: 'Pets',
			noPets: 'None',
			petsNotAllowed: 'None. Pets are not allowed at this property.',
			idDocument: 'ID document',
		},
	},
	pages: {
		payment: {
			head: {
				title: 'Pay for {property}',
			},
			title: 'Complete your payment',
			lead: 'Check your booking details, then pay to confirm your stay at {property}.',
		},
		linkExpired: {
			title: 'Payment link expired',
			description:
				"Looks like this payment link has expired, we probably didn't receive your payment in time, so the reservation has been cancelled.",
			supportText: 'Need help? {contactLink}.',
			contactLink: 'Contact Travelnest Support',
		},
		notFound: {
			title: "We couldn't find your booking",
			description:
				"The link may be incomplete or out of date. Try opening it again from your booking confirmation or messages. If it still doesn't work, get in touch and we'll help.",
			contactLink: 'Contact Travelnest Support',
		},
		fourOhFour: {
			pageTitle: 'Page not found',
			title: 'Not all those who wander are lost',
			description:
				'We couldn’t find this page. The link may be wrong or the page may have moved.',
			cta: 'Go to Travelnest',
		},
		error: {
			title: 'Something went wrong',
			description:
				'We’ve been notified and are looking into it. Please try again in a moment.',
			code: 'Error {statusCode}',
			cta: 'Try again',
			home: 'Go to Travelnest',
		},
	},
} as const;

export default en;
